import React, { useRef, useEffect, useState } from "react";
import { Check } from "lucide-react";
// import { usePostsStore } from "@/stores/posts"; // Removed unused import

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSelectedFilters: typeof defaultSelectedFilters;
  initialSelectedTags: typeof defaultSelectedTags;
  onSaveFilters: (
    filters: typeof defaultSelectedFilters,
    tags: typeof defaultSelectedTags,
  ) => void;
}

const defaultSelectedFilters = {
  forYou: true,
  recent: false,
  nearby: false,
  trending: false,
};

const defaultSelectedTags = {
  all: true,
  need: false,
  borrow: false,
  give: false,
  alert: false,
  event: false,
  share: false,
};

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  initialSelectedFilters,
  initialSelectedTags,
  onSaveFilters,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedFilters, setSelectedFilters] = useState(
    initialSelectedFilters,
  );
  const [selectedTags, setSelectedTags] = useState(initialSelectedTags);

  useEffect(() => {
    // Reset to initial state when modal is opened/reopened
    if (isOpen) {
      setSelectedFilters(initialSelectedFilters);
      setSelectedTags(initialSelectedTags);
    }
  }, [isOpen, initialSelectedFilters, initialSelectedTags]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleFilterTypeSelect = (filterType: keyof typeof selectedFilters) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const handleTagSelect = (tag: keyof typeof selectedTags) => {
    const newTags = { ...selectedTags };
    if (tag === "all") {
      Object.keys(newTags).forEach((key) => {
        newTags[key as keyof typeof selectedTags] = key === "all";
      });
    } else {
      newTags.all = false;
      newTags[tag] = !newTags[tag];
    }
    setSelectedTags(newTags);
  };

  const hasFiltersChanged = () => {
    return (
      JSON.stringify(selectedFilters) !==
        JSON.stringify(initialSelectedFilters) ||
      JSON.stringify(selectedTags) !== JSON.stringify(initialSelectedTags)
    );
  };

  const handleSave = () => {
    onSaveFilters(selectedFilters, selectedTags);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end justify-center animate-in slide-in-from-bottom duration-300">
      <div
        ref={modalRef}
        className="w-full pt-3 pb-6 bg-[#FBF6F1] rounded-tl-md rounded-tr-md inline-flex flex-col justify-start items-center gap-1.5 max-h-[90vh] overflow-y-auto"
      >
        <div className="w-20 h-[5px] bg-neutral-900 rounded-[100px] flex-shrink-0"></div>
        <div className="self-stretch flex flex-col justify-start items-start flex-grow">
          <div className="self-stretch p-4 inline-flex justify-start items-center gap-2.5">
            <div className="justify-start text-[#1E3A3A] text-xl font-semibold font-manrope leading-normal">
              Lọc bài viết theo
            </div>
          </div>
          <div className="self-stretch px-4 py-2 flex flex-col justify-start items-start gap-3">
            {Object.entries(selectedFilters).map(([key, value]) => (
              <div
                key={key}
                className="self-stretch inline-flex justify-between items-center cursor-pointer py-1"
                onClick={() =>
                  handleFilterTypeSelect(key as keyof typeof selectedFilters)
                }
              >
                <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">
                  {key === "forYou"
                    ? "Dành cho bạn"
                    : key === "recent"
                      ? "Bài viết gần đây"
                      : key === "nearby"
                        ? "Gần khu vực của bạn"
                        : "Trending"}
                </div>
                {value && (
                  <div className="size-4 relative overflow-hidden">
                    <Check className="h-4 w-4 text-[#1E3A3A]" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="self-stretch p-4 inline-flex justify-start items-center gap-2.5">
            <div className="justify-start text-[#1E3A3A] text-xl font-semibold font-manrope leading-normal">
              Lọc bài viết theo tag
            </div>
          </div>
          <div className="w-full px-4 py-2 inline-flex justify-start items-start gap-3 flex-wrap content-start">
            {Object.entries(selectedTags).map(([key, value]) => (
              <div
                key={key}
                className={`px-4 py-3 ${value ? "bg-[#E06448]" : "outline outline-1 outline-offset-[-1px] outline-[#B1B0AF]"} rounded-[6px] flex justify-start items-center gap-2.5 cursor-pointer`}
                onClick={() =>
                  handleTagSelect(key as keyof typeof selectedTags)
                }
              >
                <div
                  className={`text-center justify-start ${value ? "text-[#F4E7DA]" : "text-[#1E3A3A]"} text-sm font-normal font-sora leading-none`}
                >
                  {key === "all"
                    ? "Tất cả"
                    : key === "need"
                      ? "Cần"
                      : key === "borrow"
                        ? "Nhờ"
                        : key === "give"
                          ? "Cho"
                          : key === "alert"
                            ? "Cảnh báo"
                            : key === "event"
                              ? "Sự kiện"
                              : "Chia sẻ"}
                </div>
              </div>
            ))}
          </div>
          <div className="w-full self-stretch px-4 py-6 mt-auto flex flex-col justify-start items-start gap-2.5 ">
            <button
              className={`w-full self-stretch px-4 py-3.5 rounded-full inline-flex justify-center items-center gap-2 cursor-pointer transition-colors ${
                hasFiltersChanged()
                  ? "bg-[#E06448] hover:bg-[#D9563A]"
                  : "bg-[#D1D1D0]" // Removed group hover as it's not needed when disabled
              }`}
              onClick={handleSave}
              disabled={!hasFiltersChanged()}
            >
              <div
                className={`text-center justify-start text-sm font-medium font-sora leading-none transition-colors ${
                  hasFiltersChanged() ? "text-[#F4E7DA]" : "text-[#1E3A3A]"
                }`}
              >
                Lưu
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
